import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../api/axiosInstance";
import usePurchasingReadAccess from "./usePurchasingReadAccess";
import { inventoryPolicyBadgeClass, inventoryPolicyLabel } from "./itemsFamiliesUi";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useDocumentDialog } from "./popups/PurchasingDocumentDialog";
import { snapshotText } from "./inventorySnapshotMessages";
import InventoryFilterPopup, { InventoryWorkspaceDialog } from "./InventoryFilterPopup";
import "../PurchasingUi.css";
import "./InventorySnapshotReportTab.css";

function toISODate(dateValue) {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function safeNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

const matchesSelection = (selection, id) => !selection.length || selection.includes(String(id));
const numericSelection = (selection) => selection.map(Number).filter((id) => Number.isInteger(id) && id > 0).sort((a, b) => a - b);
function pruneSelection(setSelection, options) {
  const available = new Set(options.map((option) => option.value));
  setSelection((current) => {
    const valid = current.filter((id) => available.has(id));
    return valid.length === current.length ? current : valid;
  });
}

function labelMovementType(type) {
  const map = {
    purchase: "Purchases",
    issue_to_department: "Stock Outputs",
    inventory_count: "Inventory Count",
    adjustment: "Adjustment",
    transfer_in: "Transfer In",
    transfer_out: "Transfer Out",
    return: "Return",
    waste: "Waste",
  };

  if (!type) return "-";
  return map[type] || type.replace(/_/g, " ");
}

function buildMovementTotals(types) {
  return types.reduce((accumulator, type) => {
    accumulator[type] = { qty: 0, value: 0 };
    return accumulator;
  }, {});
}

function summarizeRows(rows, movementTypes) {
  const summary = {
    opening_qty: 0,
    opening_value: 0,
    closing_qty: 0,
    closing_value: 0,
    movement_qty_net: 0,
    movement_value_net: 0,
    expected_closing_qty: 0,
    expected_closing_value: 0,
    diff_qty: 0,
    diff_value: 0,
    movements_by_type: buildMovementTotals(movementTypes),
  };

  rows.forEach((row) => {
    summary.opening_qty += safeNumber(row.opening_qty);
    summary.opening_value += safeNumber(row.opening_value);
    summary.closing_qty += safeNumber(row.closing_qty);
    summary.closing_value += safeNumber(row.closing_value);
    summary.movement_qty_net += safeNumber(row.movement_qty_net);
    summary.movement_value_net += safeNumber(row.movement_value_net);
    summary.expected_closing_qty += safeNumber(row.expected_closing_qty);
    summary.expected_closing_value += safeNumber(row.expected_closing_value);
    summary.diff_qty += safeNumber(row.diff_qty);
    summary.diff_value += safeNumber(row.diff_value);

    movementTypes.forEach((type) => {
      const entry = row.movements_by_type?.[type] || {};
      summary.movements_by_type[type].qty += safeNumber(entry.qty);
      summary.movements_by_type[type].value += safeNumber(entry.value);
    });
  });

  return summary;
}

function groupRowsByFamilySubfamily(rows, movementTypes) {
  const groups = new Map();

  rows.forEach((row) => {
    const key = `${row.family_id || "null"}|${row.subfamily_id || "null"}`;
    if (!groups.has(key)) {
      groups.set(key, {
        family_id: row.family_id || null,
        family_name: row.family_name || "-",
        subfamily_id: row.subfamily_id || null,
        subfamily_name: row.subfamily_name || "-",
        opening_qty: 0,
        opening_value: 0,
        closing_qty: 0,
        closing_value: 0,
        movement_qty_net: 0,
        movement_value_net: 0,
        expected_closing_qty: 0,
        expected_closing_value: 0,
        diff_qty: 0,
        diff_value: 0,
        movements_by_type: buildMovementTotals(movementTypes),
      });
    }

    const target = groups.get(key);
    target.opening_qty += safeNumber(row.opening_qty);
    target.opening_value += safeNumber(row.opening_value);
    target.closing_qty += safeNumber(row.closing_qty);
    target.closing_value += safeNumber(row.closing_value);
    target.movement_qty_net += safeNumber(row.movement_qty_net);
    target.movement_value_net += safeNumber(row.movement_value_net);
    target.expected_closing_qty += safeNumber(row.expected_closing_qty);
    target.expected_closing_value += safeNumber(row.expected_closing_value);
    target.diff_qty += safeNumber(row.diff_qty);
    target.diff_value += safeNumber(row.diff_value);

    movementTypes.forEach((type) => {
      const entry = row.movements_by_type?.[type] || {};
      target.movements_by_type[type].qty += safeNumber(entry.qty);
      target.movements_by_type[type].value += safeNumber(entry.value);
    });
  });

  return Array.from(groups.values()).sort((left, right) => {
    const familyCompare = String(left.family_name || "").localeCompare(String(right.family_name || ""));
    if (familyCompare !== 0) return familyCompare;
    return String(left.subfamily_name || "").localeCompare(String(right.subfamily_name || ""));
  });
}

function createWorkbookRows(rows, movementTypes, mode) {
  return rows.map((row) => {
    const base =
      mode === "grouped"
        ? {
            Family: row.family_name || "-",
            Subfamily: row.subfamily_name || "-",
          }
        : {
            Family: row.family_name || "-",
            Subfamily: row.subfamily_name || "-",
            Category: row.category_name || "-",
            Subcategory: row.subcategory_name || "-",
            ItemCode: row.item_code || "-",
            Item: row.item_name || "-",
            InventoryPolicy: inventoryPolicyLabel(row.inventory_policy_effective),
          };

    const movementColumns = movementTypes.reduce((accumulator, type) => {
      accumulator[`${labelMovementType(type)} Qty`] = safeNumber(row.movements_by_type?.[type]?.qty);
      accumulator[`${labelMovementType(type)} Value`] = safeNumber(row.movements_by_type?.[type]?.value);
      return accumulator;
    }, {});

    return {
      ...base,
      OpeningQty: safeNumber(row.opening_qty),
      OpeningValue: safeNumber(row.opening_value),
      ...movementColumns,
      NetQty: safeNumber(row.movement_qty_net),
      NetValue: safeNumber(row.movement_value_net),
      ExpectedClosingQty: safeNumber(row.expected_closing_qty),
      ExpectedClosingValue: safeNumber(row.expected_closing_value),
      ClosingQty: safeNumber(row.closing_qty),
      ClosingValue: safeNumber(row.closing_value),
      DiffQty: safeNumber(row.diff_qty),
      DiffValue: safeNumber(row.diff_value),
    };
  });
}

const REPORT_VIEW = "purchasing.stock.current.snapshot_report";
const REPORT_EXPORT = [REPORT_VIEW, `${REPORT_VIEW}.export`];
const CLOSURE_VIEW = "purchasing.stock.current.accounting_closures.view";
const CLOSURE_CREATE = [CLOSURE_VIEW, "purchasing.stock.current.accounting_closures.create"];
const CLOSURE_OVERRIDE = [...CLOSURE_CREATE, "purchasing.stock.current.accounting_closures.override"];

export default function InventorySnapshotReportTab({ businessUnitId }) {
  const { can, requestConfig, contextKey } = usePurchasingReadAccess(businessUnitId);
  const canViewReport = can(REPORT_VIEW);
  const canViewClosures = can(CLOSURE_VIEW);
  const { themeId, languageId } = usePmsTheme();
  const t = useCallback((key, values) => snapshotText(languageId, key, values), [languageId]);
  const { confirm, dialog } = useDocumentDialog();
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [locale]);
  const currencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }), [locale]);
  const formatNumber = (value) => numberFormatter.format(safeNumber(value));
  const formatCurrency = (value) => currencyFormatter.format(safeNumber(value));
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const date = new Date(today);
    date.setDate(1);
    return toISODate(date);
  }, [today]);
  const defaultEnd = useMemo(() => toISODate(today), [today]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [movementTypes, setMovementTypes] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [viewMode, setViewMode] = useState("grouped");
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState([]);
  const [subfamilyFilter, setSubfamilyFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [subcategoryFilter, setSubcategoryFilter] = useState([]);
  const [locationSelection, setLocationSelection] = useState({ contextKey, ids: [] });
  const locationFilter = useMemo(() => locationSelection.contextKey === contextKey ? locationSelection.ids : [], [locationSelection, contextKey]);
  const setLocationFilter = (ids) => setLocationSelection({ contextKey, ids });
  const [locationChoices, setLocationChoices] = useState({ contextKey, options: [] });
  const locationOptions = locationChoices.contextKey === contextKey ? locationChoices.options : [];
  const locationKey = locationFilter.join(",");
  const locationQuery = locationKey ? `&location_ids=${encodeURIComponent(locationKey)}` : "";
  const [workspaceDialog, setWorkspaceDialog] = useState(null);
  const [varianceOnly, setVarianceOnly] = useState(false);
  const [closures, setClosures] = useState([]);
  const [closureScopeItems, setClosureScopeItems] = useState([]);
  const [closureItemFilter, setClosureItemFilter] = useState([]);
  const [closureScopeReady, setClosureScopeReady] = useState(false);
  const [closureScopeError, setClosureScopeError] = useState("");
  const [closurePreview, setClosurePreview] = useState(null);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [closureLoading, setClosureLoading] = useState(false);
  const [closureError, setClosureError] = useState("");
  const [closureNotice, setClosureNotice] = useState("");
  const [loadedPeriod, setLoadedPeriod] = useState(null);
  const [closuresListError, setClosuresListError] = useState(false);
  const ledgerRequest = useRef(0);
  const closureRequest = useRef(0);
  const closureListRequest = useRef(0);
  const scopeRef = useRef(businessUnitId);
  scopeRef.current = businessUnitId;
  const validPeriod = Boolean(startDate && endDate && startDate <= endDate);
  const reportCurrent = loadedPeriod?.locationKey === locationKey && loadedPeriod?.start === startDate && loadedPeriod?.end === endDate && loadedPeriod?.businessUnitId === businessUnitId;

  const loadLedgerReport = useCallback(async ({ start_date, end_date }) => {
    if (!can(REPORT_VIEW, true)) return;
    const requestId = ++ledgerRequest.current;
    const requestScope = businessUnitId;
    try {
      setLoading(true);
      setErrorMsg("");

      const res = await axios.get(
        `/api/purchasing/inventory/snapshot-ledger-report?start_date=${encodeURIComponent(
          start_date
        )}&end_date=${encodeURIComponent(end_date)}${locationQuery}`, requestConfig(REPORT_VIEW)
      );
      if (requestId !== ledgerRequest.current || requestScope !== scopeRef.current || !can(REPORT_VIEW, true)) return;

      const data = res.data || {};
      const nextMovementTypes = Array.isArray(data.movement_types) ? data.movement_types : [];
      const nextItems = Array.isArray(data.by_item) ? data.by_item : [];

      setMovementTypes(nextMovementTypes);
      setRawItems(nextItems);
      setLocationChoices({ contextKey, options: (Array.isArray(data.locations) ? data.locations : [])
        .map((location) => ({ value: String(location.id), label: location.name })) });
      setLoadedPeriod({ start: start_date, end: end_date, businessUnitId: requestScope, locationKey });
    } catch (error) {
      console.error("[INV][SNAPSHOT][UI][ERROR]", error);
      if (requestId !== ledgerRequest.current || requestScope !== scopeRef.current || !can(REPORT_VIEW, true)) return;
      setMovementTypes([]);
      setRawItems([]);
      setErrorMsg(error.response?.data?.error || "Failed to load snapshot ledger report.");
    } finally {
      if (requestId === ledgerRequest.current && requestScope === scopeRef.current) setLoading(false);
    }
  }, [businessUnitId, can, requestConfig, contextKey, locationKey, locationQuery]);

  useEffect(() => {
    closureRequest.current += 1;
    setClosureLoading(false);
    setRawItems([]); setLoadedPeriod(null);
    if (!validPeriod || !canViewReport) return undefined;
    loadLedgerReport({ start_date: startDate, end_date: endDate });
    return () => { ledgerRequest.current += 1; };
  }, [endDate, startDate, validPeriod, loadLedgerReport, canViewReport, contextKey]);

  const loadAccountingClosures = useCallback(async () => {
    if (!can(CLOSURE_VIEW, true)) return;
    const requestId = ++closureListRequest.current;
    const requestScope = businessUnitId;
    try {
      const res = await axios.get("/api/purchasing/inventory/accounting-closures", requestConfig(CLOSURE_VIEW));
      if (scopeRef.current !== requestScope || requestId !== closureListRequest.current || !can(CLOSURE_VIEW, true)) return;
      setClosures(Array.isArray(res.data?.closures) ? res.data.closures : []);
      setClosuresListError(false);
    } catch (error) {
      console.error("[INV][ACCOUNTING-CLOSURES][UI][LIST][ERROR]", error);
      if (scopeRef.current === requestScope && requestId === closureListRequest.current && can(CLOSURE_VIEW, true)) setClosuresListError(true);
    }
  }, [businessUnitId, can, requestConfig]);

  useEffect(() => {
    setClosures([]);
    setClosurePreview(null);
    setSelectedClosure(null);
    setClosureError("");
    setClosureNotice("");
    setClosuresListError(false);
    loadAccountingClosures();
    return () => { closureRequest.current += 1; closureListRequest.current += 1; };
  }, [businessUnitId, loadAccountingClosures, contextKey]);

  useEffect(() => {
    let cancelled = false;
    setClosureScopeItems([]); setClosureItemFilter([]); setClosureScopeReady(false); setClosureScopeError("");
    if (!can(CLOSURE_CREATE, true)) return undefined;
    axios.get("/api/purchasing/inventory/accounting-closures/scope-options", requestConfig(CLOSURE_CREATE)).then(({ data }) => {
      if (cancelled || !can(CLOSURE_CREATE, true)) return;
      setClosureScopeItems((Array.isArray(data?.items) ? data.items : []).filter((item) => Number.isSafeInteger(Number(item.item_id)) && Number(item.item_id) > 0));
      setClosureScopeReady(true);
    }).catch((error) => {
      if (!cancelled && can(CLOSURE_CREATE, true)) setClosureScopeError(error.response?.data?.error || "Failed to load permitted inventory items.");
    });
    return () => { cancelled = true; };
  }, [can, requestConfig, contextKey]);

  const familyOptions = useMemo(() => {
    return Array.from(
      new Map(
        rawItems
          .filter((row) => row.family_id && row.family_name)
          .map((row) => [
            String(row.family_id),
            { value: String(row.family_id), label: String(row.family_name) },
          ])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
  }, [rawItems]);

  const scopedSubfamilies = useMemo(() => {
    return rawItems.filter((row) => matchesSelection(familyFilter, row.family_id));
  }, [familyFilter, rawItems]);

  const subfamilyOptions = useMemo(() => {
    return Array.from(
      new Map(
        scopedSubfamilies
          .filter((row) => row.subfamily_id && row.subfamily_name)
          .map((row) => [
            String(row.subfamily_id),
            { value: String(row.subfamily_id), label: String(row.subfamily_name) },
          ])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
  }, [scopedSubfamilies]);

  const scopedCategories = useMemo(() => {
    return scopedSubfamilies.filter(
      (row) => matchesSelection(subfamilyFilter, row.subfamily_id)
    );
  }, [scopedSubfamilies, subfamilyFilter]);

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Map(
        scopedCategories
          .filter((row) => row.category_id && row.category_name)
          .map((row) => [
            String(row.category_id),
            { value: String(row.category_id), label: String(row.category_name) },
          ])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
  }, [scopedCategories]);

  const scopedSubcategories = useMemo(() => {
    return scopedCategories.filter(
      (row) => matchesSelection(categoryFilter, row.category_id)
    );
  }, [categoryFilter, scopedCategories]);

  const subcategoryOptions = useMemo(() => {
    return Array.from(
      new Map(
        scopedSubcategories
          .filter((row) => row.subcategory_id && row.subcategory_name)
          .map((row) => [
            String(row.subcategory_id),
            { value: String(row.subcategory_id), label: String(row.subcategory_name) },
          ])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label));
  }, [scopedSubcategories]);

  useEffect(() => {
    if (reportCurrent && !loading && !errorMsg) pruneSelection(setFamilyFilter, familyOptions);
  }, [familyOptions, reportCurrent, loading, errorMsg]);

  useEffect(() => {
    if (reportCurrent && !loading && !errorMsg) pruneSelection(setSubfamilyFilter, subfamilyOptions);
  }, [subfamilyOptions, reportCurrent, loading, errorMsg]);

  useEffect(() => {
    if (reportCurrent && !loading && !errorMsg) pruneSelection(setCategoryFilter, categoryOptions);
  }, [categoryOptions, reportCurrent, loading, errorMsg]);

  useEffect(() => {
    if (reportCurrent && !loading && !errorMsg) pruneSelection(setSubcategoryFilter, subcategoryOptions);
  }, [subcategoryOptions, reportCurrent, loading, errorMsg]);

  const filterItems = useCallback((items) => {
    const query = search.trim().toLowerCase();

    return items.filter((row) => {
      if (!matchesSelection(familyFilter, row.family_id)) return false;
      if (!matchesSelection(subfamilyFilter, row.subfamily_id)) return false;
      if (!matchesSelection(categoryFilter, row.category_id)) return false;
      if (!matchesSelection(subcategoryFilter, row.subcategory_id)) return false;
      if (varianceOnly && Math.abs(safeNumber(row.diff_qty)) < 0.0001 && Math.abs(safeNumber(row.diff_value)) < 0.0001) {
        return false;
      }

      if (query) {
        const haystack = [
          row.family_name,
          row.subfamily_name,
          row.category_name,
          row.subcategory_name,
          row.item_code,
          row.item_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [categoryFilter, familyFilter, search, subcategoryFilter, subfamilyFilter, varianceOnly]);
  const filteredItems = useMemo(() => filterItems(rawItems), [filterItems, rawItems]);

  const groupedRows = useMemo(
    () => groupRowsByFamilySubfamily(filteredItems, movementTypes),
    [filteredItems, movementTypes]
  );

  const totals = useMemo(
    () => summarizeRows(filteredItems, movementTypes),
    [filteredItems, movementTypes]
  );

  const visibleFamilies = useMemo(() => {
    return new Set(filteredItems.map((row) => row.family_id ?? row.family_name ?? "-")).size;
  }, [filteredItems]);

  const accountingClosureScope = useMemo(() => {
    const scope = {};
    if (familyFilter.length) scope.family_ids = numericSelection(familyFilter);
    if (subfamilyFilter.length) scope.subfamily_ids = numericSelection(subfamilyFilter);
    if (categoryFilter.length) scope.category_ids = numericSelection(categoryFilter);
    if (subcategoryFilter.length) scope.subcategory_ids = numericSelection(subcategoryFilter);
    scope.item_ids = closureScopeItems.filter((row) => matchesSelection(closureItemFilter, row.item_id) &&
      matchesSelection(familyFilter, row.family_id) && matchesSelection(subfamilyFilter, row.subfamily_id) &&
      matchesSelection(categoryFilter, row.category_id) && matchesSelection(subcategoryFilter, row.subcategory_id))
      .map((row) => Number(row.item_id)).sort((a, b) => a - b);
    return scope;
  }, [categoryFilter, familyFilter, subcategoryFilter, subfamilyFilter, closureScopeItems, closureItemFilter]);

  const accountingScopeLabel = useMemo(() => {
    const labels = [];
    [[familyFilter, familyOptions], [subfamilyFilter, subfamilyOptions], [categoryFilter, categoryOptions], [subcategoryFilter, subcategoryOptions]].forEach(([selection, options]) => {
      const names = options.filter((option) => selection.includes(option.value)).map((option) => option.label);
      if (names.length) labels.push(names.join(", "));
    });

    if (closureItemFilter.length) labels.push(closureScopeItems.filter((item) => closureItemFilter.includes(String(item.item_id))).map((item) => item.item_name || item.item_code).join(", "));
    return labels.length ? `${t("Authorized inventory items")} · ${labels.join(" / ")}` : "Authorized inventory items";
  }, [
    categoryFilter,
    categoryOptions,
    familyFilter,
    familyOptions,
    subcategoryFilter,
    subcategoryOptions,
    subfamilyFilter,
    subfamilyOptions,
    closureItemFilter,
    closureScopeItems,
    t,
  ]);

  const accountingScopeKey = JSON.stringify(accountingClosureScope);
  useEffect(() => {
    setClosurePreview(null);
    setSelectedClosure(null);
    setClosureError("");
    setClosureNotice("");
    closureRequest.current += 1;
    setClosureLoading(false);
  }, [accountingScopeKey, endDate]);

  const previewAccountingClosure = useCallback(async () => {
    if (!endDate || !closureScopeReady || !accountingClosureScope.item_ids.length || !can(CLOSURE_CREATE, true)) return;
    const requestId = ++closureRequest.current;
    try {
      setClosureLoading(true);
      setClosureError("");
      setClosureNotice("");

      const res = await axios.post("/api/purchasing/inventory/accounting-closures/preview", {
        closing_date: endDate,
        scope: accountingClosureScope,
        scope_label: accountingScopeLabel,
        include_zero_lines: true,
      }, requestConfig(CLOSURE_CREATE));

      if (requestId !== closureRequest.current || !can(CLOSURE_CREATE, true)) return;
      setClosurePreview(res.data || null);
      setSelectedClosure(null);
    } catch (error) {
      console.error("[INV][ACCOUNTING-CLOSURES][UI][PREVIEW][ERROR]", error);
      if (requestId !== closureRequest.current || !can(CLOSURE_CREATE, true)) return;
      setClosurePreview(null);
      setClosureError(error.response?.data?.error || "Failed to preview accounting close.");
    } finally {
      if (requestId === closureRequest.current) setClosureLoading(false);
    }
  }, [accountingClosureScope, accountingScopeLabel, endDate, can, requestConfig, closureScopeReady]);

  const loadClosureDetail = useCallback(async (closureId) => {
    if (!closureId || !can(CLOSURE_VIEW, true)) return;
    const requestId = ++closureRequest.current;
    try {
      setClosureLoading(true);
      setClosureError("");
      const res = await axios.get(`/api/purchasing/inventory/accounting-closures/${closureId}`, requestConfig(CLOSURE_VIEW));
      if (requestId !== closureRequest.current || !can(CLOSURE_VIEW, true)) return;
      setSelectedClosure(res.data || null);
      setClosurePreview(null);
    } catch (error) {
      console.error("[INV][ACCOUNTING-CLOSURES][UI][DETAIL][ERROR]", error);
      if (requestId !== closureRequest.current || !can(CLOSURE_VIEW, true)) return;
      setSelectedClosure(null);
      setClosureError(error.response?.data?.error || "Failed to load accounting close.");
    } finally {
      if (requestId === closureRequest.current) setClosureLoading(false);
    }
  }, [can, requestConfig]);

  const postAccountingClosure = useCallback(async () => {
    if (!endDate || !closureScopeReady || !accountingClosureScope.item_ids.length || !can(CLOSURE_CREATE, true)) return;
    const requestScope = businessUnitId;
    const confirmed = await confirmRef.current(t("Post the accounting close for {scope} on {date}? This saves an accounting record.", { scope: t(accountingScopeLabel), date: endDate }));
    if (!confirmed || requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;

    const payload = {
      closing_date: endDate,
      title: `${accountingScopeLabel} - ${endDate}`,
      scope: accountingClosureScope,
      scope_label: accountingScopeLabel,
      include_zero_lines: true,
    };

    const createClosure = async (allowOverlaps) => {
      const res = await axios.post("/api/purchasing/inventory/accounting-closures", {
        ...payload,
        allow_overlaps: allowOverlaps,
      }, requestConfig(allowOverlaps ? CLOSURE_OVERRIDE : CLOSURE_CREATE));
      return res.data;
    };

    try {
      setClosureLoading(true);
      setClosureError("");
      setClosureNotice("");

      const data = await createClosure(false);
      if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
      setClosureNotice("Accounting close posted.");
      setClosurePreview(null);
      await loadAccountingClosures();
      if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
      if (data?.closure?.id) await loadClosureDetail(data.closure.id);
    } catch (error) {
      if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
      if (error.response?.status === 409 && can(CLOSURE_OVERRIDE, true)) {
        const overlaps = error.response?.data?.overlaps;
        const retryConfirmed = await confirmRef.current(t("This close overlaps {count} item lines already posted for {date}. Post it anyway?", { count: overlaps?.count || 0, date: endDate }));
        if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
        if (retryConfirmed && can(CLOSURE_OVERRIDE, true)) {
          try {
            const data = await createClosure(true);
            if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
            setClosureNotice("Accounting close posted with overlap warning.");
            setClosurePreview(null);
            await loadAccountingClosures();
            if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
            if (data?.closure?.id) await loadClosureDetail(data.closure.id);
            return;
          } catch (retryError) {
            if (requestScope !== scopeRef.current || !can(CLOSURE_CREATE, true)) return;
            console.error("[INV][ACCOUNTING-CLOSURES][UI][CREATE-RETRY][ERROR]", retryError);
            setClosureError(retryError.response?.data?.error || "Failed to post accounting close.");
          }
        } else {
          setClosureError("Accounting close was not posted because it overlaps an existing close.");
        }
      } else {
        console.error("[INV][ACCOUNTING-CLOSURES][UI][CREATE][ERROR]", error);
        setClosureError(error.response?.data?.error || "Failed to post accounting close.");
      }
    } finally {
      if (requestScope === scopeRef.current) setClosureLoading(false);
    }
  }, [
    accountingClosureScope,
    accountingScopeLabel,
    endDate,
    loadAccountingClosures,
    loadClosureDetail,
    businessUnitId,
    can,
    requestConfig,
    closureScopeReady,
    t,
  ]);

  const tableMinWidth = useMemo(() => {
    const base = viewMode === "grouped" ? 10 : 14;
    return Math.max(1420, (base + movementTypes.length * 2) * 108);
  }, [movementTypes.length, viewMode]);

  const dateError =
    startDate && endDate && startDate > endDate
      ? "Start date must be before or equal to end date."
      : "";

  const handleLoad = () => {
    if (!startDate || !endDate || dateError) return;
    loadLedgerReport({ start_date: startDate, end_date: endDate });
  };

  const exportScope = JSON.stringify([contextKey, startDate, endDate, locationKey, categoryFilter, familyFilter, search, subcategoryFilter, subfamilyFilter, varianceOnly]);
  const exportScopeRef = useRef(exportScope);
  const exportEpochRef = useRef(0);
  if (exportScopeRef.current !== exportScope) { exportScopeRef.current = exportScope; exportEpochRef.current += 1; }
  const exportToExcel = async () => {
    if (exporting || !filteredItems.length || !reportCurrent || loading || errorMsg || !validPeriod || !can(REPORT_EXPORT, true)) return;
    const requestScope = exportScope;
    const requestEpoch = exportEpochRef.current;
    setExporting(true); setExportError("");
    try {
      const response = await axios.get(`/api/purchasing/inventory/snapshot-ledger-report?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}${locationQuery}&export=1`, requestConfig(REPORT_EXPORT));
      if (exportEpochRef.current !== requestEpoch || exportScopeRef.current !== requestScope || !can(REPORT_EXPORT, true)) return;
      const freshItems = Array.isArray(response.data?.by_item) ? response.data.by_item : [];
      const exportItems = filterItems(freshItems);
      const exportTypes = Array.isArray(response.data?.movement_types) ? response.data.movement_types : [];
      const exportGroupedRows = groupRowsByFamilySubfamily(exportItems, exportTypes);
      const exportTotals = summarizeRows(exportItems, exportTypes);
      setRawItems(freshItems); setMovementTypes(exportTypes);
      if (!exportItems.length) return;

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet([
        { Metric: "Business Unit", Value: businessUnitId || "-" },
        { Metric: "Start Date", Value: startDate },
        { Metric: "End Date", Value: endDate },
        { Metric: "Locations", Value: locationFilter.length ? locationOptions.filter((option) => locationFilter.includes(option.value)).map((option) => option.label).join(", ") : "All" },
        { Metric: "Valuation Basis", Value: locationFilter.length ? "Location quantities at historical item average cost" : "Global snapshots" },
        { Metric: "Visible Families", Value: new Set(exportItems.map((row) => row.family_id ?? row.family_name ?? "-")).size },
        { Metric: "Visible Items", Value: exportItems.length },
        { Metric: "Opening Value", Value: safeNumber(exportTotals.opening_value) },
        { Metric: "Net Movement Value", Value: safeNumber(exportTotals.movement_value_net) },
        { Metric: "Closing Value", Value: safeNumber(exportTotals.closing_value) },
        { Metric: "Diff Value", Value: safeNumber(exportTotals.diff_value) },
      ]);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      const groupedSheet = XLSX.utils.json_to_sheet(
        createWorkbookRows(exportGroupedRows, exportTypes, "grouped")
      );
      XLSX.utils.book_append_sheet(workbook, groupedSheet, "FamilyLens");

      const itemSheet = XLSX.utils.json_to_sheet(
        createWorkbookRows(exportItems, exportTypes, "items")
      );
      XLSX.utils.book_append_sheet(workbook, itemSheet, "ItemLens");

      XLSX.writeFile(
        workbook,
        `inventory_snapshot_${startDate || "start"}_${endDate || "end"}.xlsx`
      );
    } catch (error) {
      if (exportEpochRef.current === requestEpoch && exportScopeRef.current === requestScope && can(REPORT_VIEW, true)) setExportError(error.response?.data?.error || "The report could not be exported.");
    } finally { setExporting(false); }
  };

  const renderGroupedTable = () => (
    <table className="ivs-table" aria-label={t(viewMode === "grouped" ? "Family Lens" : "Item Lens")} style={{ minWidth: `${tableMinWidth}px` }}>
      <thead>
        <tr>
          <th>{t("Family")}</th>
          <th>{t("Subfamily")}</th>
          <th className="ivs-num">{t("Opening Qty")}</th>
          <th className="ivs-num">{t("Opening Value")}</th>
          {movementTypes.map((type) => (
            <React.Fragment key={type}>
              <th className="ivs-num">{t(labelMovementType(type))} · {t("Qty")}</th>
              <th className="ivs-num">{t(labelMovementType(type))} · {t("Value")}</th>
            </React.Fragment>
          ))}
          <th className="ivs-num">{t("Net Qty")}</th>
          <th className="ivs-num">{t("Net Value")}</th>
          <th className="ivs-num">{t("Expected Qty")}</th>
          <th className="ivs-num">{t("Closing Qty")}</th>
          <th className="ivs-num">{t("Closing Value")}</th>
          <th className="ivs-num">{t("Diff Qty")}</th>
          <th className="ivs-num">{t("Diff Value")}</th>
        </tr>
      </thead>
      <tbody>
        {groupedRows.map((row) => (
          <tr key={`${row.family_id || "null"}-${row.subfamily_id || "null"}`}>
            <td className="ivs-strong">{row.family_name || "-"}</td>
            <td className="ivs-muted">{row.subfamily_name || "-"}</td>
            <td className="ivs-num">{formatNumber(row.opening_qty)}</td>
            <td className="ivs-num">{formatCurrency(row.opening_value)}</td>
            {movementTypes.map((type) => (
              <React.Fragment key={`${row.family_id || "null"}-${type}`}>
                <td className={`ivs-num ${safeNumber(row.movements_by_type?.[type]?.qty) < 0 ? "ivs-negative" : ""}`}>
                  {formatNumber(row.movements_by_type?.[type]?.qty)}
                </td>
                <td className={`ivs-num ${safeNumber(row.movements_by_type?.[type]?.value) < 0 ? "ivs-negative" : ""}`}>
                  {formatCurrency(row.movements_by_type?.[type]?.value)}
                </td>
              </React.Fragment>
            ))}
            <td className={`ivs-num ${safeNumber(row.movement_qty_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
              {formatNumber(row.movement_qty_net)}
            </td>
            <td className={`ivs-num ${safeNumber(row.movement_value_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
              {formatCurrency(row.movement_value_net)}
            </td>
            <td className="ivs-num">{formatNumber(row.expected_closing_qty)}</td>
            <td className="ivs-num">{formatNumber(row.closing_qty)}</td>
            <td className="ivs-num">{formatCurrency(row.closing_value)}</td>
            <td className={`ivs-num ${safeNumber(row.diff_qty) === 0 ? "ivs-muted" : "ivs-negative"}`}>
              {formatNumber(row.diff_qty)}
            </td>
            <td className={`ivs-num ${safeNumber(row.diff_value) === 0 ? "ivs-muted" : "ivs-negative"}`}>
              {formatCurrency(row.diff_value)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>{t("Grand Total")}</td>
          <td className="ivs-num">{formatNumber(totals.opening_qty)}</td>
          <td className="ivs-num">{formatCurrency(totals.opening_value)}</td>
          {movementTypes.map((type) => (
            <React.Fragment key={`totals-${type}`}>
              <td className="ivs-num">{formatNumber(totals.movements_by_type?.[type]?.qty)}</td>
              <td className="ivs-num">{formatCurrency(totals.movements_by_type?.[type]?.value)}</td>
            </React.Fragment>
          ))}
          <td className={`ivs-num ${safeNumber(totals.movement_qty_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
            {formatNumber(totals.movement_qty_net)}
          </td>
          <td className={`ivs-num ${safeNumber(totals.movement_value_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
            {formatCurrency(totals.movement_value_net)}
          </td>
          <td className="ivs-num">{formatNumber(totals.expected_closing_qty)}</td>
          <td className="ivs-num">{formatNumber(totals.closing_qty)}</td>
          <td className="ivs-num">{formatCurrency(totals.closing_value)}</td>
          <td className={`ivs-num ${safeNumber(totals.diff_qty) === 0 ? "ivs-muted" : "ivs-negative"}`}>
            {formatNumber(totals.diff_qty)}
          </td>
          <td className={`ivs-num ${safeNumber(totals.diff_value) === 0 ? "ivs-muted" : "ivs-negative"}`}>
            {formatCurrency(totals.diff_value)}
          </td>
        </tr>
      </tfoot>
    </table>
  );

  const renderItemTable = () => (
    <table className="ivs-table" aria-label={t(viewMode === "grouped" ? "Family Lens" : "Item Lens")} style={{ minWidth: `${tableMinWidth}px` }}>
      <thead>
        <tr>
          <th>{t("Item")}</th>
          <th>{t("Code")}</th>
          <th className="ivs-num">{t("Opening Qty")}</th>
          <th className="ivs-num">{t("Opening Value")}</th>
          {movementTypes.map((type) => (
            <React.Fragment key={type}>
              <th className="ivs-num">{t(labelMovementType(type))} · {t("Qty")}</th>
              <th className="ivs-num">{t(labelMovementType(type))} · {t("Value")}</th>
            </React.Fragment>
          ))}
          <th className="ivs-num">{t("Net Qty")}</th>
          <th className="ivs-num">{t("Net Value")}</th>
          <th className="ivs-num">{t("Expected Qty")}</th>
          <th className="ivs-num">{t("Closing Qty")}</th>
          <th className="ivs-num">{t("Closing Value")}</th>
          <th className="ivs-num">{t("Diff Qty")}</th>
          <th className="ivs-num">{t("Diff Value")}</th>
          <th>{t("Family")}</th>
          <th>{t("Subfamily")}</th>
          <th>{t("Category")}</th>
          <th>{t("Subcategory")}</th>
          <th>{t("Inventory Policy")}</th>
        </tr>
      </thead>
      <tbody>
        {filteredItems.map((row) => (
          <tr key={row.item_id}>
            <td className="ivs-strong">{row.item_name || "-"}</td>
            <td className="ivs-strong">{row.item_code || "-"}</td>
            <td className="ivs-num">{formatNumber(row.opening_qty)}</td>
            <td className="ivs-num">{formatCurrency(row.opening_value)}</td>
            {movementTypes.map((type) => (
              <React.Fragment key={`${row.item_id}-${type}`}>
                <td className={`ivs-num ${safeNumber(row.movements_by_type?.[type]?.qty) < 0 ? "ivs-negative" : ""}`}>
                  {formatNumber(row.movements_by_type?.[type]?.qty)}
                </td>
                <td className={`ivs-num ${safeNumber(row.movements_by_type?.[type]?.value) < 0 ? "ivs-negative" : ""}`}>
                  {formatCurrency(row.movements_by_type?.[type]?.value)}
                </td>
              </React.Fragment>
            ))}
            <td className={`ivs-num ${safeNumber(row.movement_qty_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
              {formatNumber(row.movement_qty_net)}
            </td>
            <td className={`ivs-num ${safeNumber(row.movement_value_net) < 0 ? "ivs-negative" : "ivs-positive"}`}>
              {formatCurrency(row.movement_value_net)}
            </td>
            <td className="ivs-num">{formatNumber(row.expected_closing_qty)}</td>
            <td className="ivs-num">{formatNumber(row.closing_qty)}</td>
            <td className="ivs-num">{formatCurrency(row.closing_value)}</td>
            <td className={`ivs-num ${safeNumber(row.diff_qty) === 0 ? "ivs-muted" : "ivs-negative"}`}>
              {formatNumber(row.diff_qty)}
            </td>
            <td className={`ivs-num ${safeNumber(row.diff_value) === 0 ? "ivs-muted" : "ivs-negative"}`}>
              {formatCurrency(row.diff_value)}
            </td>
            <td className="ivs-muted">{row.family_name || "-"}</td>
            <td className="ivs-muted">{row.subfamily_name || "-"}</td>
            <td className="ivs-muted">{row.category_name || "-"}</td>
            <td className="ivs-muted">{row.subcategory_name || "-"}</td>
            <td>
              <span className={inventoryPolicyBadgeClass(row.inventory_policy_effective)}>
                {t(inventoryPolicyLabel(row.inventory_policy_effective))}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const hasFilters = Boolean(locationFilter.length || search.trim() || varianceOnly || [familyFilter, subfamilyFilter, categoryFilter, subcategoryFilter].some((value) => value.length));
  const clearFilters = () => { setLocationFilter([]); setSearch(""); setVarianceOnly(false); setFamilyFilter([]); setSubfamilyFilter([]); setCategoryFilter([]); setSubcategoryFilter([]); };
  const reportReady = canViewReport && reportCurrent && !loading && !errorMsg && validPeriod;
  const closureReady = /^\d{4}-\d{2}-\d{2}$/.test(endDate) && closureScopeReady && accountingClosureScope.item_ids.length > 0;
  const closureItemOptions = closureScopeItems.map((item) => ({ value: String(item.item_id), label: [item.item_code, item.item_name].filter(Boolean).join(" · ") }));
  const hierarchyFields = [
    ["Family", familyFilter, familyOptions, setFamilyFilter],
    ["Subfamily", subfamilyFilter, subfamilyOptions, setSubfamilyFilter],
    ["Category", categoryFilter, categoryOptions, setCategoryFilter],
    ["Subcategory", subcategoryFilter, subcategoryOptions, setSubcategoryFilter],
  ];

  if (!canViewReport && !canViewClosures) return <div role="alert">{t("You don't have permission to access this section.")}</div>;

  return (
    <div className="pilcore-purchasing-ui ivs-shell" data-theme={themeId}>
      {dialog}
      {exportError && <div className="ivs-noteError" role="alert">{t(exportError)}</div>}
      <h3 className="ivs-srOnly">{t("Inventory reconciliation")}</h3>
      <header className="ivs-toolbar" aria-label={t("Snapshot Ledger")}>
        <div className="ivs-toolbarRow ivs-primaryRow">
          <section className="ivs-period" aria-label={t("Reporting period")}>
          <div className="ivs-field">
            <label className="ivs-label" htmlFor="ivs-start">{t("Opening Date")}</label>
            <input id="ivs-start" type="date" className="ivs-input" value={startDate} disabled={closureLoading}
              onChange={(event) => setStartDate(event.target.value)} aria-invalid={!validPeriod} />
          </div>
          <div className="ivs-field">
            <label className="ivs-label" htmlFor="ivs-end">{t("Closing Date")}</label>
            <input id="ivs-end" type="date" className="ivs-input" value={endDate} disabled={closureLoading}
              onChange={(event) => setEndDate(event.target.value)} aria-invalid={!validPeriod} />
          </div>
          </section>
          <div className="ivs-field ivs-search">
            <label className="ivs-srOnly" htmlFor="ivs-search">{t("Search")}</label>
            <input id="ivs-search" type="search" className="ivs-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("Search item or code…")} />
          </div>
          {can(REPORT_EXPORT) && <button type="button" className="ivs-btn ivs-btn--export" onClick={exportToExcel} disabled={exporting || !reportReady || !filteredItems.length}>{t("Export Excel")}</button>}
          {canViewClosures && <button type="button" className="ivs-btn" onClick={() => setWorkspaceDialog("accounting")}>{t("Accounting Close")}</button>}
          <button type="button" className="ivs-btn ivs-help" aria-label={t("Report help")} title={t("Report help")} onClick={() => setWorkspaceDialog("help")}>?</button>
        </div>
        {canViewReport && <div className="ivs-toolbarRow ivs-filterRow">
          <section className="ivs-filters" aria-label={t("Filter inventory")}>
            {hierarchyFields.map(([label, value, options, onChange]) => (
              <InventoryFilterPopup key={label} label={t(label)} value={value} options={options} onChange={onChange} disabled={!reportReady || closureLoading} languageId={languageId} />
            ))}
            <InventoryFilterPopup label={t("Location")} value={locationFilter} options={locationOptions} onChange={setLocationFilter} disabled={!canViewReport || loading || closureLoading || !locationOptions.length} languageId={languageId} />
          </section>
          <div className="ivs-toggleGroup" role="group" aria-label={t("Report view")}>
            <button type="button" className={`ivs-toggle ${viewMode === "grouped" ? "ivs-toggle--active" : ""}`} aria-pressed={viewMode === "grouped"} onClick={() => setViewMode("grouped")}>{t("Family Lens")}</button>
            <button type="button" className={`ivs-toggle ${viewMode === "items" ? "ivs-toggle--active" : ""}`} aria-pressed={viewMode === "items"} onClick={() => setViewMode("items")}>{t("Item Lens")}</button>
            <button type="button" className={`ivs-toggle ${varianceOnly ? "ivs-toggle--active" : ""}`} aria-pressed={varianceOnly} onClick={() => setVarianceOnly((value) => !value)}>{t("Reconciliation Only")}</button>
            {hasFilters && <button type="button" className="ivs-btn" disabled={closureLoading} onClick={clearFilters}>{t("Clear filters")}</button>}
          </div>
        </div>}
      </header>

      {canViewReport && <>
      {locationFilter.length > 0 && <p className="ivs-noteRow" role="status">{t("Location values use the item's historical average cost. The location filter does not restrict accounting closes.")}</p>}
      <div className="ivs-infoRow">
        <dl className="ivs-summary" aria-live="polite">
        {[["Opening Value", totals.opening_value], ["Net Movement Value", totals.movement_value_net], ["Closing Value", totals.closing_value], ["Diff Value", totals.diff_value]].map(([label, value]) => (
          <div key={label}><dt>{t(label)}</dt><dd className={label === "Diff Value" && Math.abs(value) >= 0.0001 ? "ivs-negative" : ""}>{reportReady ? formatCurrency(value) : "—"}</dd></div>
        ))}
        </dl>
        <div className="ivs-rowCounts" aria-live="polite">
          <span>{t("Items")}: <strong>{reportReady ? filteredItems.length : "—"}</strong></span>
          <span>{t("Families")}: <strong>{reportReady ? visibleFamilies : "—"}</strong></span>
        </div>
      </div>

      <section className="ivs-tableCard" aria-busy={loading}>
        <div className="ivs-tableWrap" tabIndex={0} role="region" aria-label={t("Inventory reconciliation")}>
          {errorMsg ? <div className="ivs-empty"><span className="ivs-noteError" role="alert">{t(errorMsg)}</span><button type="button" className="ivs-btn" onClick={handleLoad} disabled={!validPeriod}>{t("Retry")}</button></div>
            : !validPeriod ? <div className="ivs-empty" role="alert">{t(dateError || "Choose an opening and closing date.")}</div>
            : !reportReady ? <div className="ivs-empty" role="status">{t("Loading the selected period…")}</div>
            : !filteredItems.length ? <div className="ivs-empty">{t(rawItems.length ? "No snapshot rows match the selected hierarchy filters." : "No inventory data for this period.")}</div>
            : viewMode === "grouped" ? renderGroupedTable() : renderItemTable()}
        </div>
      </section></>}

      {workspaceDialog === "help" && <InventoryWorkspaceDialog title={t("Report help")} onClose={() => setWorkspaceDialog(null)}>
        <div className="ivs-shell ivs-helpContent">
          <p>{t("Opening + net movements = expected closing. Difference = actual closing − expected closing.")}</p>
          <p>{t("Quantities may combine different item units; compare values for a financial total.")}</p>
          <p>{t("Scroll horizontally to see every movement and reconciliation column.")}</p>
          <p>{t("Filters accept several selections. Choices within one filter are combined; different filters must all match.")}</p>
        </div>
      </InventoryWorkspaceDialog>}
      {workspaceDialog === "accounting" && canViewClosures && <InventoryWorkspaceDialog title={t("Accounting Close")} onClose={() => setWorkspaceDialog(null)}>
        <div className="ivs-shell ivs-closurePanel">
        <div className="ivs-closureBody">
          {can(CLOSURE_CREATE) && <InventoryFilterPopup label={t("Authorized inventory items")} value={closureItemFilter} options={closureItemOptions} onChange={setClosureItemFilter} disabled={!closureScopeReady || closureLoading} languageId={languageId} />}
          {closureScopeError && <div className="ivs-noteError" role="alert">{t(closureScopeError)}</div>}
          <div className="ivs-closureHead">
            <div>
              <p className="ivs-closureCopy">{t("Scope")}: <strong>{t(accountingScopeLabel)}</strong> · {t("Closing date")}: <strong>{endDate || "—"}</strong></p>
              <p className="ivs-closureCopy">{t("Review the preview before posting. A close is a saved accounting record, not a report filter.")}</p>
            </div>
            <div className="ivs-buttonGroup">
              {can(CLOSURE_CREATE) && <button type="button" className="ivs-btn" onClick={previewAccountingClosure} disabled={closureLoading || !closureReady}>{t(closureLoading ? "Working..." : "Preview Close")}</button>}
              {can(CLOSURE_CREATE) && <button type="button" className="ivs-btn ivs-btn--primary" onClick={postAccountingClosure} disabled={closureLoading || !closureReady || !closurePreview?.summary}>{t("Post Close")}</button>}
            </div>
          </div>
          <p className="ivs-closureCopy">{t("The close includes the permitted items selected here, the hierarchy filters and closing date. Report search and differences do not restrict it.")}</p>
          {closurePreview?.summary && <>
            <div className="ivs-closureStats">
              {[
                ["Lines", closurePreview.summary.line_count || 0], ["Qty", formatNumber(closurePreview.summary.total_qty)],
                ["Value", formatCurrency(closurePreview.summary.total_value)], ["Overlaps", closurePreview.overlaps?.count || 0],
              ].map(([label, value]) => <div className="ivs-closureStat" key={label}><div className="ivs-closureStatLabel">{t(label)}</div><div className="ivs-closureStatValue">{value}</div></div>)}
            </div>
            {Boolean(closurePreview.overlaps?.count) && <div className="ivs-closureWarning" role="status">{t("Some item lines are already posted in another close for this date.")}</div>}
          </>}
          {selectedClosure && <div className="ivs-closureStats">
            {[
              ["Closed Lines", selectedClosure.line_count || 0], ["Closed Qty", formatNumber(selectedClosure.total_qty)], ["Closed Value", formatCurrency(selectedClosure.total_value)],
            ].map(([label, value]) => <div className="ivs-closureStat" key={label}><div className="ivs-closureStatLabel">{t(label)}</div><div className="ivs-closureStatValue">{value}</div></div>)}
          </div>}
          {closureNotice && <div className="ivs-noteRow" role="status">{t(closureNotice)}</div>}
          {closureError && <div className="ivs-noteError" role="alert">{t(closureError)}</div>}
          <h4>{t("Recent closes")}</h4>
          {closuresListError ? <div className="ivs-noteRow"><span className="ivs-noteError" role="alert">{t("Failed to load accounting closes.")}</span><button type="button" className="ivs-btn" onClick={loadAccountingClosures}>{t("Retry")}</button></div>
            : closures.length ? <div className="ivs-closureList">{closures.slice(0, 8).map((closure) => (
              <button type="button" key={closure.id} className={`ivs-closurePill ${selectedClosure?.id === closure.id ? "ivs-closurePill--active" : ""}`}
                aria-pressed={selectedClosure?.id === closure.id} onClick={() => loadClosureDetail(closure.id)} disabled={closureLoading}>
                {closure.closing_date} · {t(closure.scope_label || closure.title || "Close #{id}", { id: closure.id })}
              </button>
            ))}</div> : <p className="ivs-closureCopy">{t("No accounting closes yet.")}</p>}
        </div>
        </div>
      </InventoryWorkspaceDialog>}
    </div>
  );
}
